/**
 * Agenda (tela inicial, plano 11.1 função 1; docs/design/pranchas/00-agenda-aprovada.png).
 * Celular e tablet: o dia em linhas e o painel Agora ao alcance do polegar.
 * Computador: a semana (WeekView). Deslizar muda o dia; toque longo no livre bloqueia.
 */
import {
  type AgendaListRow,
  addDays,
  buildAgendaRows,
  daySummary,
  formatDayLong,
  formatHourOf,
  formatMoneyShort,
  formatWeekdayNumber,
  mergeFreeRows,
  nowAndNext,
  relativeDayLabel,
  todayIn,
  toMs,
  utcToZoned,
} from '@lindeza/core';
import { type AgendaItem, useDayAgenda, useDayCounts, usePendingRequests, useProfessional, useTomorrow } from '@lindeza/core/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AccessBanner, InstallBanner } from '../../app/Banners';
import { useMediaQuery } from '../../app/useMediaQuery';
import { IconCalendarDays, IconChevronLeft, IconChevronRight, IconWhatsapp } from '../../components/Icons';
import { SyncBadge } from '../../components/SyncBadge';
import { Empty } from '../../components/ui';
import './agenda.css';
import { AgendaSheets, type SheetState } from './AgendaSheets';
import { AgoraPanel } from './AgoraPanel';
import { ApptRow, BlockRow, FreeRow } from './AgendaRows';
import { ConflictBanner, PendingBanner } from './DayBanners';
import { WeekView } from './WeekView';

export function Agenda() {
  const prof = useProfessional();
  const tz = prof?.timezone ?? 'America/Sao_Paulo';
  const today = todayIn(tz);
  const { date: dateParam } = useParams();
  const date = dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) ? dateParam : today;
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const [sheet, setSheet] = useState<SheetState>(null);
  const [now, setNow] = useState(() => Date.now());
  const wide = useMediaQuery('(min-width: 1280px)');
  const [view, setView] = useState<'week' | 'day'>('week');

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(t);
  }, []);

  // Notificações: "Amanhã: N atendimentos" abre /?lembretes=amanha;
  // "Novo agendamento" abre /agenda/AAAA-MM-DD?atendimento=ID; "Marcar" da doca abre ?marcar=1.
  useEffect(() => {
    const appointmentId = search.get('atendimento');
    if (search.get('lembretes') === 'amanha') {
      setSheet({ kind: 'tomorrow' });
      search.delete('lembretes');
      setSearch(search, { replace: true });
    } else if (appointmentId) {
      setSheet({ kind: 'appt', id: appointmentId });
      search.delete('atendimento');
      setSearch(search, { replace: true });
    } else if (search.get('marcar') === '1') {
      // "Marcar horário" da ficha manda ?cliente=ID: ela já aparece primeiro na lista.
      const clientId = search.get('cliente') ?? undefined;
      setSheet({ kind: 'pick', date, ...(clientId ? { clientId } : {}) });
      search.delete('marcar');
      search.delete('cliente');
      setSearch(search, { replace: true });
    }
  }, [search, setSearch, date]);

  const goTo = (d: string) => navigate(d === today ? '/' : `/agenda/${d}`, { replace: true });
  const sheets = <AgendaSheets sheet={sheet} setSheet={setSheet} today={today} now={now} timezone={tz} />;

  if (wide && view === 'week') {
    return (
      <>
        <WeekView date={date} today={today} now={now} timezone={tz} onPickDate={goTo} onDayView={() => setView('day')} setSheet={setSheet} />
        {sheets}
      </>
    );
  }

  return (
    <>
      <DayView date={date} today={today} now={now} timezone={tz} goTo={goTo} setSheet={setSheet} onWeekView={wide ? () => setView('week') : undefined} />
      {sheets}
    </>
  );
}

function DayView({
  date,
  today,
  now,
  timezone: tz,
  goTo,
  setSheet,
  onWeekView,
}: {
  date: string;
  today: string;
  now: number;
  timezone: string;
  goTo: (d: string) => void;
  setSheet: (s: SheetState) => void;
  onWeekView?: () => void;
}) {
  const prof = useProfessional();
  const day = useDayAgenda(date);
  const tomorrow = useTomorrow();
  const pending = usePendingRequests();
  const isToday = date === today;

  const rows = useMemo<AgendaListRow<AgendaItem>[]>(
    () =>
      day
        ? mergeFreeRows(
            buildAgendaRows({
              date,
              timezone: tz,
              items: day.items,
              blocks: day.blocks,
              openRanges: day.openRanges,
              now,
              stepMin: 10,
            }),
          )
        : [],
    [day, date, tz, now],
  );
  const { current, next } = useMemo(() => (day && isToday ? nowAndNext(day.items, now) : { current: null, next: null }), [day, isToday, now]);
  const panelItem = current ?? next;
  const after = current ? next : panelItem && day ? (nowAndNext(day.items, toMs(panelItem.appointment.start_utc) + 1).next ?? null) : null;
  const summary = day ? daySummary(day.items) : null;

  // Deslizar para o lado muda o dia
  const touch = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (t) touch.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touch.current;
    const t = e.changedTouches[0];
    touch.current = null;
    if (!start || !t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) > 70 && Math.abs(dy) < 50) goTo(addDays(date, dx < 0 ? 1 : -1));
  };

  const localHour = utcToZoned(now, tz).hour;
  const showReminders = isToday && (tomorrow?.length ?? 0) > 0 && localHour >= (prof?.reminder_hour ?? 19) && !remindersDismissed(today);
  const closedDay = day && day.openRanges.length === 0;
  // Só a de agora sai da lista (está no painel); a próxima continua na lista também.
  const visible = rows.filter((r) => !(r.kind === 'appointment' && current && r.item.appointment.id === current.appointment.id));
  const hasAnything = visible.some((r) => r.kind !== 'now');
  const firstFuture = isToday ? visible.findIndex((r) => r.kind !== 'now' && toMs(r.start) >= now) : -1;
  const label = relativeDayLabel(date, today);
  const longDate = formatDayLong(date).replace(/^./, (c) => c.toUpperCase());

  return (
    <div className={`ag-screen${panelItem ? '' : ' ag-screen--nopanel'}`}>
      <main className="ag-day" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <header className="ag-head">
          <div>
            <h1 className="ag-date">
              {['Hoje', 'Amanhã', 'Ontem'].includes(label) && <span className="sr-only">{label}, </span>}
              {longDate}
            </h1>
            {summary && summary.count > 0 && (
              <p className="ag-sum">
                {summary.count} {summary.count === 1 ? 'atendimento' : 'atendimentos'}, {formatMoneyShort(summary.expectedCents)} previstos
              </p>
            )}
          </div>
          <div className="row">
            {onWeekView ? (
              <button className="btn btn--outline btn--sm" onClick={onWeekView}>
                Semana
              </button>
            ) : (
              // no computador o selo fica no trilho
              <SyncBadge />
            )}
          </div>
        </header>

        <DayStrip date={date} today={today} onPick={goTo} />

        <AccessBanner />
        <InstallBanner />
        {pending && pending.length > 0 && <PendingBanner items={pending} timezone={tz} onOpen={(id) => setSheet({ kind: 'appt', id })} />}
        {day && day.overlaps.length > 0 && <ConflictBanner overlaps={day.overlaps} timezone={tz} onReschedule={(id) => setSheet({ kind: 'appt', id })} />}

        <div className="ag-list" data-testid="timeline">
          {!day ? null : !hasAnything ? (
            closedDay ? (
              <Empty
                icon={<IconCalendarDays />}
                action={
                  <button className="btn btn--outline" onClick={() => setSheet({ kind: 'pick', date })}>
                    Marcar mesmo assim
                  </button>
                }
              >
                Dia fechado. Você não abriu horários para este dia.
              </Empty>
            ) : current ? (
              <p className="ag-sec">Mais ninguém depois dela hoje.</p>
            ) : (
              <Empty
                icon={<IconCalendarDays />}
                action={
                  <Link className="btn btn--primary" to="/divulgar">
                    Compartilhar meu link
                  </Link>
                }
              >
                Nenhum atendimento {isToday ? 'hoje' : 'neste dia'}. Mande seu link no WhatsApp e no Instagram.
              </Empty>
            )
          ) : (
            visible.map((row, i) => {
              const sec =
                isToday && i === firstFuture ? (
                  <p key={`sec-${row.start}`} className="ag-sec">
                    Depois
                  </p>
                ) : isToday && i === 0 && firstFuture !== 0 ? (
                  <p key="sec-antes" className="ag-sec">
                    Antes
                  </p>
                ) : null;
              let el: React.ReactNode = null;
              switch (row.kind) {
                case 'now':
                  el = panelItem ? null : (
                    <div key="now" className="ag-now" aria-label="Agora">
                      <b>{formatHourOf(row.start, tz)}</b>
                    </div>
                  );
                  break;
                case 'free':
                  el = (
                    <FreeRow
                      key={`f-${row.start}`}
                      gap={row}
                      onTap={() => setSheet({ kind: 'new', start: row.start })}
                      onLongPress={() => setSheet({ kind: 'block', start: row.start })}
                    />
                  );
                  break;
                case 'block':
                  el = (
                    <BlockRow
                      key={`b-${row.block.id}`}
                      start={row.block.start_utc}
                      end={row.block.end_utc}
                      reason={row.block.reason}
                      timezone={tz}
                      onOpen={() => setSheet({ kind: 'block', start: row.block.start_utc, blockId: row.block.id })}
                    />
                  );
                  break;
                case 'appointment':
                  el = <ApptRow key={row.item.appointment.id} item={row.item} timezone={tz} onOpen={() => setSheet({ kind: 'appt', id: row.item.appointment.id })} />;
                  break;
              }
              return sec ? [sec, el] : el;
            })
          )}
          {day && day.inactive.length > 0 && (
            <details className="ag-inactive">
              <summary>
                {day.inactive.length} {day.inactive.length === 1 ? 'cancelado ou falta' : 'cancelados ou faltas'}
              </summary>
              {day.inactive.map((item) => (
                <ApptRow key={item.appointment.id} item={item} timezone={tz} onOpen={() => setSheet({ kind: 'appt', id: item.appointment.id })} />
              ))}
            </details>
          )}
        </div>

        {showReminders && (
          <div className="ag-tomorrow">
            <div className="ag-tomorrow-txt">
              <b>
                Amanhã: {tomorrow!.length} {tomorrow!.length === 1 ? 'cliente' : 'clientes'}
              </b>
              <span>Lembretes ainda não enviados</span>
            </div>
            <button className="btn btn--whatsapp" onClick={() => setSheet({ kind: 'tomorrow' })}>
              <IconWhatsapp /> Mandar lembretes
            </button>
          </div>
        )}

        <div className="ag-daynav">
          <button className="btn btn--ghost btn--sm" onClick={() => goTo(addDays(date, -1))} aria-label="Dia anterior">
            <IconChevronLeft /> Anterior
          </button>
          {!isToday && (
            <button className="btn btn--ghost btn--sm" onClick={() => goTo(today)}>
              Hoje
            </button>
          )}
          <button className="btn btn--ghost btn--sm" onClick={() => goTo(addDays(date, 1))} aria-label="Próximo dia">
            Próximo <IconChevronRight />
          </button>
        </div>
      </main>

      {panelItem && (
        <AgoraPanel
          item={panelItem}
          after={after}
          now={now}
          timezone={tz}
          onCharge={() => setSheet({ kind: 'pay', id: panelItem.appointment.id })}
          onOpen={() => setSheet({ kind: 'appt', id: panelItem.appointment.id })}
        />
      )}
    </div>
  );
}

function remindersDismissed(today: string): boolean {
  try {
    return localStorage.getItem(`lindeza:reminders:${today}`) === '1';
  } catch {
    return false;
  }
}

function DayStrip({ date, today, onPick }: { date: string; today: string; onPick: (d: string) => void }) {
  const from = date < today ? date : addDays(today, -2);
  const days = Array.from({ length: 16 }, (_, i) => addDays(from, i));
  const counts = useDayCounts(days[0]!, days[days.length - 1]!);
  const selectedRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [date]);
  return (
    <div className="ag-days" role="tablist" aria-label="Dias">
      {days.map((d) => {
        const { weekday, day } = formatWeekdayNumber(d);
        const n = counts?.get(d) ?? 0;
        return (
          <button
            key={d}
            ref={d === date ? selectedRef : undefined}
            role="tab"
            aria-selected={d === date}
            aria-label={`${relativeDayLabel(d, today)}, ${n} ${n === 1 ? 'atendimento' : 'atendimentos'}`}
            className={d === today ? 'today' : undefined}
            onClick={() => onPick(d)}
          >
            <span className="wd">{d === today ? 'hoje' : weekday}</span>
            <span className="d">{day}</span>
            <span className="n">
              {Array.from({ length: Math.min(n, 4) }, (_, i) => (
                <span key={i} />
              ))}
            </span>
          </button>
        );
      })}
    </div>
  );
}
